<template>
  <div class="flex items-center flex-col">
    <!-- Show loading only when fetching and we don't have data yet (avoids stuck loading when API already returned) -->
    <div v-if="isLoading && !campaigns.length" class="items-center flex text-base justify-center">
      <spinner color-scheme="primary" />
      <span>{{ $t('CAMPAIGN.LIST.LOADING_MESSAGE') }}</span>
    </div>
    <div v-else class="w-full">
      <empty-state v-if="showEmptyResult" :title="emptyMessage" />
      <!-- WhatsApp & email campaigns: same tabular layout -->
      <div
        v-else-if="!showEmptyResult"
        class="campaigns-table-wrap w-full rounded-lg overflow-hidden"
      >
        <div class="campaigns-table-wrap w-full rounded-lg ">
          <table class="campaigns-woot-table w-full">
            <thead>
              <tr class="campaigns-header-row">
                <th class="campaigns-th">
                  {{ $t('CAMPAIGN.LIST.TABLE_HEADER.TITLE') }}
                </th>
                <th class="campaigns-th">
                  {{ $t('CAMPAIGN.LIST.TABLE_HEADER.STATUS') }}
                </th>
                <th class="campaigns-th">
                  {{ $t('CAMPAIGN.LIST.TABLE_HEADER.INBOX') }}
                </th>
                <th class="campaigns-th">
                  {{ $t('CAMPAIGN.LIST.TABLE_HEADER.CREATED_AT') }}
                </th>
                <th class="campaigns-th-action">
                  {{ $t('CAMPAIGN.LIST.TABLE_HEADER.ACTION') }}
                </th>
              </tr>
            </thead>

            <tbody class="campaigns-body">
              <tr
                v-for="campaign in paginatedCampaigns"
                :key="campaign.id"
                class="campaigns-row"
              >
                <td
                  class="campaigns-td-title campaigns-td-title--clickable"
                  @click="onTableRowClick(campaign)"
                >
                  {{ campaign.title }}
                </td>

                <td class="campaigns-td">
                  <woot-label
                    small
                    :title="getCampaignStatusLabel(campaign)"
                    :color-scheme="getCampaignStatusColor(campaign)"
                    class="text-xs"
                  />
                </td>

                <td class="campaigns-td-secondary">
                  {{ (campaign.inbox && campaign.inbox.name) || '—' }}
                </td>

                <td class="campaigns-td-secondary campaigns-td-nowrap">
                  {{ formatCampaignCreatedAt(campaign.created_at) }}
                </td>

                <td class="campaigns-td-action" @click.stop>
                  <div class="flex items-center gap-2">
                    <woot-button
                      variant="link"
                      icon="dismiss-circle"
                      size="small"
                      color-scheme="alert"
                      @click="$emit('delete', campaign)"
                    >
                      {{ $t('CAMPAIGN.LIST.BUTTONS.DELETE') }}
                    </woot-button>
                  </div>
                </td>
              </tr>
            </tbody>
          </table>
        </div>
        <!-- Fixed pagination footer matching the card layout -->
        <div class="campaigns-table-pagination-fixed">
          <table-footer
            v-if="paginationTotalCount > 0"
            :current-page="paginationCurrentPage"
            :total-count="paginationTotalCount"
            :page-size="paginationPageSize"
            @page-change="onPageChange"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<script>
import { mixin as clickaway } from 'vue-clickaway';
import Spinner from 'shared/components/Spinner.vue';
import EmptyState from 'dashboard/components/widgets/EmptyState.vue';
import campaignMixin from 'shared/mixins/campaignMixin';
import CampaignCard from './CampaignCard.vue';
import TableFooter from 'dashboard/components/widgets/TableFooter';

const getTimestamp = (dateValue) => {
  if (!dateValue) return 0;
  
  if (typeof dateValue === 'number') {
    return dateValue < 946684800000 ? dateValue * 1000 : dateValue;
  }
  
  const date = new Date(dateValue);
  return date.getTime() || 0;
};

const getCampaignTimestamp = (campaign) => {
  return campaign.scheduled_at
    ? getTimestamp(campaign.scheduled_at)
    : getTimestamp(campaign.created_at);
};

const formatCreatedAtDisplay = dateValue => {
  if (!dateValue) return '—';
  const ts = getTimestamp(dateValue);
  if (!ts) return '—';
  return new Date(ts).toLocaleString(undefined, {
    month: 'short',
    day: 'numeric',
    year: 'numeric',
    hour: '2-digit',
    minute: '2-digit',
  });
};

export default {
  components: {
    EmptyState,
    Spinner,
    CampaignCard,
    TableFooter,
  },

  mixins: [clickaway, campaignMixin],

  data() {
    return {
      localPage: 1,
      localPerPage: 20,
    };
  },

  props: {
    campaigns: {
      type: Array,
      default: () => [],
    },
    totalCount: {
      type: Number,
      default: -1,
    },
    currentPage: {
      type: Number,
      default: 1,
    },
    pageSize: {
      type: Number,
      default: 20,
    },
    showEmptyResult: {
      type: Boolean,
      default: false,
    },
    isLoading: {
      type: Boolean,
      default: false,
    },
  },

  computed: {
    isServerSidePagination() {
      return this.totalCount !== -1;
    },
    useCampaignsTableLayout() {
      return this.isWhatsappType || this.isEmailCampaignType ;
    },
    sortedCampaigns() {
      if (!this.campaigns || this.campaigns.length === 0) {
        return [];
      }

      return [...this.campaigns].sort((a, b) => {
        const timeA = getCampaignTimestamp(a);
        const timeB = getCampaignTimestamp(b);
        
        return timeB - timeA;
      });
    },
    paginatedCampaigns() {
      if (this.isServerSidePagination) {
        return this.sortedCampaigns;
      }
      const start = (this.localPage - 1) * this.localPerPage;
      return this.sortedCampaigns.slice(start, start + this.localPerPage);
    },
    paginationTotalCount() {
      return this.isServerSidePagination
        ? this.totalCount
        : this.sortedCampaigns.length;
    },
    paginationCurrentPage() {
      return this.isServerSidePagination
        ? this.currentPage
        : this.localPage;
    },
    paginationPageSize() {
      return this.isServerSidePagination
        ? this.pageSize
        : this.localPerPage;
    },
    currentInboxId() {
      return this.$route.params.inboxId;
    },
    inbox() {
      return this.$store.getters['inboxes/getInbox'](this.currentInboxId);
    },
    inboxes() {
      if (this.isOngoingType) {
        return this.$store.getters['inboxes/getWebsiteInboxes'];
      }
      if (this.isWhatsappType) {
        return this.$store.getters['inboxes/getWhatsappInboxes'];
      }
      if (this.isSmartAiEmailType) {
        return this.$store.getters['inboxes/getEmailInboxes'];
      }
      if (this.isEmailCampaignType) {
        return this.$store.getters['inboxes/getEmailInboxes'];
      }
      return this.$store.getters['inboxes/getTwilioInboxes'];
    },
    emptyMessage() {
      if (this.isOngoingType) {
        return this.inboxes.length
          ? this.$t('CAMPAIGN.ONGOING.404')
          : this.$t('CAMPAIGN.ONGOING.INBOXES_NOT_FOUND');
      }
      if (this.isWhatsappType) {
        return this.inboxes.length
          ? this.$t('CAMPAIGN.WHATSAPP.404')
          : this.$t('CAMPAIGN.WHATSAPP.INBOXES_NOT_FOUND');
      }
      if (this.isSmartAiEmailType) {
        return this.inboxes.length
          ? this.$t('CAMPAIGN.SMART_AI_EMAIL.404')
          : this.$t('CAMPAIGN.SMART_AI_EMAIL.INBOXES_NOT_FOUND');
      }
      if (this.isEmailCampaignType) {
        return this.inboxes.length
          ? this.$t('CAMPAIGN.EMAIL.404')
          : this.$t('CAMPAIGN.EMAIL.INBOXES_NOT_FOUND');
      }

      return this.inboxes.length
        ? this.$t('CAMPAIGN.ONE_OFF.404')
        : this.$t('CAMPAIGN.ONE_OFF.INBOXES_NOT_FOUND');
    },
  },
  methods: {
    formatCampaignCreatedAt: formatCreatedAtDisplay,
    getCampaignStatusLabel(campaign) {
      return campaign.campaign_status === 'completed'
        ? this.$t('CAMPAIGN.LIST.STATUS.COMPLETED')
        : this.$t('CAMPAIGN.LIST.STATUS.ACTIVE');
    },
    getCampaignStatusColor(campaign) {
      return campaign.campaign_status === 'completed'
        ? 'secondary'
        : 'success';
    },
    onTableRowClick(campaign) {
      if (this.isSmartAiEmailType) {
        this.$router.push({
          name: 'smart_ai_email_campaign_contacts',
          params: {
            accountId: this.$route.params.accountId,
            campaigns_id: campaign.display_id || campaign.id,
          },
        });
        return;
      }
      if (this.isEmailCampaignType) {
        this.$router.push({
          name: 'email_campaigns_messages',
          params: {
            accountId: this.$route.params.accountId,
            campaigns_id: campaign.id,
          },
        });
        return;
      }
      if (this.isWhatsappType) {
        this.$router.push({
          name: 'whatsapp_campaigns_messages',
          params: {
            accountId: this.$route.params.accountId,
            campaigns_id: campaign.id,
          },
        });
      }
    },
    onPageChange(page) {
      if (this.isServerSidePagination) {
        this.$emit('page-change', page);
      } else {
        this.localPage = page;
      }
    },
  },
};
</script>

<style lang="scss" scoped>
.campaigns-table-layout {
  display: flex;
  flex-direction: column;
  padding-bottom: 1.7rem;
}

.campaigns-table-wrap {
  padding: 0;
  width: 100%;
  overflow-x: auto;
  overflow-y: hidden;
}

/* Table */
.campaigns-woot-table {
  min-width: 500px;
  border-collapse: separate;
  border-spacing: 0;
  border: 1px solid #cfd2d6;
  border-radius: 8px;
  overflow: hidden;
  background-color: white;
}

/* Dark mode */
.dark .campaigns-woot-table {
  border-color: #1e293b;
}

/* Header */
.campaigns-woot-table thead th {
  padding: 10px 16px;
  font-size: 12px;
  text-transform: uppercase;
  font-weight: 600;
  text-align: left;
  letter-spacing: 0.5px;

  @apply bg-slate-50 dark:bg-slate-800;
  color: #475569;

  border-right: 1px solid #cfd2d6;
  border-bottom: 1px solid #cfd2d6;
}

/* Header last column */
.campaigns-woot-table thead th:last-child {
  border-right: none;
}

/* Body rows */
.campaigns-woot-table tbody tr {
  transition: background-color 0.15s ease;
}

/* Only campaign name cell is clickable */
.campaigns-td-title--clickable {
  cursor: pointer;
}

.campaigns-td-title--clickable:hover {
  text-decoration: underline;
}

/* Hover */
.campaigns-woot-table tbody tr:hover td {
  background-color:  #f9fafb;
}

/* Table cells */
.campaigns-woot-table tbody td {
  padding: 10px 16px;
  font-size: 13px;
  color: #334155;

  border-right: 1px solid #cfd2d6;
  border-bottom: 1px solid #cfd2d6;
}

/* Last column */
.campaigns-woot-table tbody td:last-child {
  border-right: none;
}

/* Last row */
.campaigns-woot-table tbody tr:last-child td {
  border-bottom: none;
}

/* Dark mode cells */
.dark .campaigns-woot-table thead th {
  background: #1e293b;
  color: #e2e8f0;
  border-color: #334155;
}

.dark .campaigns-woot-table tbody td {
  background: #020617;
  color: #e2e8f0;
  border-color: #334155;
}

.dark .campaigns-woot-table tbody tr:hover td {
  background: #1e293b;
}

.campaigns-td-nowrap {
  white-space: nowrap;
}

.campaigns-td-action button:hover {
  text-decoration: none;
}
</style>

<style lang="scss" scoped>
.campaigns-list-with-pagination {
  display: flex;
  flex-direction: column;
}

/* Reserve space at bottom so last cards aren't hidden under fixed pagination */
.campaigns-grid-wrap {
  margin-bottom: var(--space-normal);
  padding-bottom: 1.7rem;
}

/* Fixed at bottom of viewport so pagination is always visible; aligned to right of primary sidebar */
.campaigns-table-pagination-fixed {
  position: fixed;
  bottom: 0;
  left: 3.375rem;
  right: 0;
  z-index: 20;
  background: #f8fafc;
  border-top: 1px solid #e0e0e0;
  box-shadow: 0 -2px 12px rgba(0, 0, 0, 0.08);

  .dark & {
    background: var(--s-900);
    border-top-color: var(--s-700);
    box-shadow: 0 -2px 12px rgba(0, 0, 0, 0.25);
  }
}

/* RTL: sidebar on the right */
[dir='rtl'] .campaigns-table-pagination-fixed {
  left: 0;
  right: 14.375rem;
}
</style>
